import React, { useEffect } from "react";
import { CheckCircle, Home, CreditCard } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { usePaymentCore } from "@/features/payment";
import { useStripeCheckout } from "@/features/payment";
import { usePricingStore } from "@/features/pricing/store/pricing-store";
import { useTranslation } from "react-i18next";

export const PaymentSuccessPage: React.FC = () => {
  const { completePayment, successVariant, sessionId: storedSessionId } =
    usePaymentCore();
  const { checkSessionStatus } = useStripeCheckout();
  const { openPricingModalWithTopup } = usePricingStore();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  // Öncelik store'da: /subscription-success adresine gelen kullanıcıda URL
  // temizlendiği için parametre yalnızca doğrudan ziyarette hayatta kalıyor.
  const sessionId = storedSessionId ?? searchParams.get("session_id");

  // Same modal, different copy when shown for a subscription plan change.
  const titleKey =
    successVariant === "upgrade"
      ? "payment.success.upgradeTitle"
      : successVariant === "downgrade"
        ? "payment.success.downgradeTitle"
        : "payment.success.title";
  const descriptionKey =
    successVariant === "upgrade"
      ? "payment.success.upgradeDescription"
      : successVariant === "downgrade"
        ? "payment.success.downgradeDescription"
        : "payment.success.description";

  useEffect(() => {
    if (!sessionId) return;
    console.log(
      "🔄 PaymentSuccessPage: checkSessionStatus called for sessionId:",
      sessionId
    );
    checkSessionStatus(sessionId);
  }, [sessionId]); // checkSessionStatus dependency'sini kaldırdım

  const handleGoHome = () => {
    completePayment();
  };

  const handleBuyMoreCredits = () => {
    completePayment();
    // Kısa bir gecikme ile pricing modal'ı aç
    setTimeout(() => {
      openPricingModalWithTopup();
    }, 100);
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <div className="mb-6">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
        {t(titleKey)}
      </h2>

      <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md">
        {t(descriptionKey)}
      </p>

      <div className="flex flex-col sm:flex-row gap-4 w-full max-w-sm">
        <Button
          onClick={handleGoHome}
          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
        >
          <Home className="w-4 h-4 mr-2" />
          {t("payment.success.goHome")}
        </Button>

        <Button
          variant="outline"
          onClick={handleBuyMoreCredits}
          className="flex-1"
        >
          <CreditCard className="w-4 h-4 mr-2" />
          {t("payment.success.buyMoreCredits")}
        </Button>
      </div>

      <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800">
        <p className="text-sm text-green-700 dark:text-green-300">
          💡 {t("payment.success.tip")}
        </p>
      </div>
    </div>
  );
};
